/**
 * Pull Judge.me customer review photos into the PDP image carousel —
 * only when they resemble the product's own media (skip people / lifestyle shots).
 */

const FINGERPRINT_SIZE = 8;
/** Mean absolute RGB distance vs product media; lower = closer. Person photo on Fae ~0.24. */
const MAX_PRODUCT_DISTANCE = 0.17;
/** Portrait frames that don't closely match product media are treated as non-product. */
const PORTRAIT_ASPECT_MAX = 0.88;
const PORTRAIT_DISTANCE_MAX = 0.12;

function getPictureUrls(picture) {
  if (!picture) return null;
  if (typeof picture === "string") {
    return { src: picture, thumbSrc: picture };
  }
  const src =
    picture.huge || picture.original || picture.compact || picture.small;
  const thumbSrc =
    picture.compact || picture.small || picture.huge || picture.original || src;
  if (!src) return null;
  return { src, thumbSrc };
}

function collectPicturesFromPayload(data, max) {
  const collected = [];
  const seen = new Set();

  const pushPicture = (picture, review, index) => {
    if (collected.length >= max * 3) return; // gather extras; filter later
    const urls = getPictureUrls(picture);
    if (!urls || seen.has(urls.src)) return;
    seen.add(urls.src);
    const idBase =
      (typeof picture === "object" && (picture.uuid || picture.id)) ||
      review?.uuid ||
      review?.created_at ||
      collected.length;
    collected.push({
      id: `jdgm-review-${idBase}-${index}`,
      src: urls.src,
      thumbSrc: urls.thumbSrc,
      alt: review?.reviewer_name
        ? `Customer photo of the product from ${review.reviewer_name}`
        : "Customer photo of the product",
      reviewerName: review?.reviewer_name || "",
    });
  };

  (data?.photo_gallery || []).forEach((entry, index) => {
    (entry?.pictures_urls || []).forEach((picture, picIndex) => {
      pushPicture(picture, entry, `${index}-${picIndex}`);
    });
  });

  (data?.reviews || []).forEach((review, index) => {
    (review?.pictures_urls || []).forEach((picture, picIndex) => {
      pushPicture(picture, review, `${index}-${picIndex}`);
    });
  });

  return collected;
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

async function loadImageDimensions(src) {
  try {
    const img = await loadImageElement(src);
    return {
      width: img.naturalWidth || 1024,
      height: img.naturalHeight || 1024,
      img,
    };
  } catch (error) {
    return { width: 1024, height: 1024, img: null };
  }
}

function fingerprintFromImage(img) {
  const canvas = document.createElement("canvas");
  canvas.width = FINGERPRINT_SIZE;
  canvas.height = FINGERPRINT_SIZE;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, FINGERPRINT_SIZE, FINGERPRINT_SIZE);
  const { data } = ctx.getImageData(0, 0, FINGERPRINT_SIZE, FINGERPRINT_SIZE);
  const values = new Float32Array(FINGERPRINT_SIZE * FINGERPRINT_SIZE * 3);
  for (let i = 0, j = 0; i < data.length; i += 4, j += 3) {
    values[j] = data[i] / 255;
    values[j + 1] = data[i + 1] / 255;
    values[j + 2] = data[i + 2] / 255;
  }
  return values;
}

function fingerprintDistance(a, b) {
  if (!a || !b || a.length !== b.length) return 1;
  let total = 0;
  for (let i = 0; i < a.length; i++) total += Math.abs(a[i] - b[i]);
  return total / a.length;
}

async function buildProductFingerprints(urls = []) {
  const fingerprints = [];
  for (const url of urls) {
    if (!url) continue;
    try {
      const img = await loadImageElement(url);
      const fp = fingerprintFromImage(img);
      if (fp) fingerprints.push(fp);
    } catch (error) {
      // CORS or load failure — skip this reference image
    }
  }
  return fingerprints;
}

function getProductImageUrls(gallery) {
  const fromData = gallery.dataset.judgemeProductImages;
  if (fromData) {
    try {
      const parsed = JSON.parse(fromData);
      if (Array.isArray(parsed) && parsed.length) return parsed.filter(Boolean);
    } catch (error) {
      // fall through to DOM scrape
    }
  }

  const urls = [];
  gallery
    .querySelectorAll(
      "[data-gallery] [data-swiper-slide]:not([data-review-photo]) img",
    )
    .forEach((img) => {
      const src = img.currentSrc || img.src;
      if (src && !urls.includes(src)) urls.push(src);
    });
  return urls;
}

function resemblesProductPhoto(picture, productFingerprints) {
  if (!picture.fingerprint || !productFingerprints.length) return false;

  const minDistance = Math.min(
    ...productFingerprints.map((fp) =>
      fingerprintDistance(picture.fingerprint, fp),
    ),
  );
  picture.productDistance = minDistance;

  const aspect =
    picture.width && picture.height ? picture.width / picture.height : 1;

  // Portrait lifestyle / selfie shots that don't closely match product media.
  if (aspect < PORTRAIT_ASPECT_MAX && minDistance > PORTRAIT_DISTANCE_MAX) {
    return false;
  }

  return minDistance <= MAX_PRODUCT_DISTANCE;
}

async function fetchJudgeMeReviewPhotos({
  shopDomain,
  productId,
  max,
  productImageUrls,
}) {
  if (!shopDomain || !productId) return [];

  const params = new URLSearchParams({
    url: shopDomain,
    shop_domain: shopDomain,
    platform: "shopify",
    product_id: String(productId),
    per_page: "50",
  });

  const endpoint = `https://cdn.judge.me/reviews/reviews_for_widget?${params}`;
  const response = await fetch(endpoint, {
    credentials: "omit",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) return [];

  const data = await response.json();
  const pictures = collectPicturesFromPayload(data, max);
  if (!pictures.length) return [];

  const productFingerprints = await buildProductFingerprints(productImageUrls);
  // Without product references we cannot tell product shots from people photos.
  if (!productFingerprints.length) return [];

  const prepared = [];
  for (const picture of pictures) {
    const dimensions = await loadImageDimensions(picture.src);
    const fingerprint = dimensions.img
      ? fingerprintFromImage(dimensions.img)
      : null;
    const candidate = {
      ...picture,
      width: dimensions.width,
      height: dimensions.height,
      fingerprint,
    };
    if (resemblesProductPhoto(candidate, productFingerprints)) {
      prepared.push(candidate);
    }
    if (prepared.length >= max) break;
  }

  return prepared.map(({ fingerprint, productDistance, ...rest }) => rest);
}

async function waitForGalleryReady(gallery, attempts = 40) {
  await customElements.whenDefined("gallery-section");
  for (let i = 0; i < attempts; i++) {
    if (typeof gallery.appendExternalImages === "function" && gallery.elements) {
      return true;
    }
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }
  return typeof gallery.appendExternalImages === "function";
}

async function injectJudgeMeReviewPhotos(gallery) {
  if (!gallery || gallery.dataset.reviewPhotosLoaded === "true") return;

  const shopDomain =
    gallery.dataset.judgemeShopDomain || window.Shopify?.shop || "";
  const productId = gallery.dataset.judgemeProductId;
  const max = Number(gallery.dataset.judgemeMaxPhotos || 8);

  if (!shopDomain || !productId || max < 1) return;

  try {
    const ready = waitForGalleryReady(gallery);
    const productImageUrls = getProductImageUrls(gallery);
    const photosPromise = fetchJudgeMeReviewPhotos({
      shopDomain,
      productId,
      max,
      productImageUrls,
    });
    const [isReady, photos] = await Promise.all([ready, photosPromise]);
    if (!isReady || !photos.length) {
      gallery.dataset.reviewPhotosLoaded = "true";
      return;
    }

    const added = gallery.appendExternalImages(photos);
    gallery.dataset.reviewPhotosLoaded = "true";
    if (added > 0) {
      gallery.dataset.reviewPhotosCount = String(added);
    }
  } catch (error) {
    console.warn("[judgeme-gallery-photos]", error);
  }
}

function initJudgeMeGalleryPhotos() {
  document
    .querySelectorAll("gallery-section[data-judgeme-review-photos]")
    .forEach((gallery) => {
      injectJudgeMeReviewPhotos(gallery);
    });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initJudgeMeGalleryPhotos);
} else {
  initJudgeMeGalleryPhotos();
}
